/**
 * v3.18-1298 PAYMENT-008B P5 — Reversal history block for a single
 * Credit Card Authorization row.
 *
 * Renders the complete payment lifecycle for one saved
 * authorization: original charge, every refund, every void, plus a
 * running remaining balance so operators immediately see the state
 * without leaving the client profile.
 */
import React, { useEffect, useState } from 'react';
import { RefreshCcw, Undo2, XCircle, DollarSign, User, Clock } from 'lucide-react';
import { Button } from './ui/button';
import api from '../lib/api';

const fmt = (n) => {
  if (n === null || n === undefined || n === '') return '$0.00';
  const v = Number(n);
  if (Number.isNaN(v)) return '$0.00';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const fmtDT = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); }
  catch (_e) { return iso; }
};

const rowMeta = (tx) => {
  const type = (tx.type || '').toLowerCase();
  const status = (tx.status || '').toLowerCase();
  if (type === 'refund')  return { icon: Undo2,   color: 'text-purple-700', bg: 'bg-purple-50', label: 'Refund' };
  if (type === 'void')    return { icon: XCircle, color: 'text-slate-700',  bg: 'bg-slate-100', label: 'Void'   };
  if (status === 'declined') return { icon: XCircle, color: 'text-rose-700', bg: 'bg-rose-50', label: 'Declined' };
  return { icon: DollarSign, color: 'text-emerald-700', bg: 'bg-emerald-50', label: 'Original charge' };
};

const ReversalHistoryBlock = ({ authorizationId, refreshKey }) => {
  const [rows, setRows] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!authorizationId) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/payments/engine/authorizations/${authorizationId}/history`);
        if (!cancelled) {
          setRows(data.transactions || []);
          setBalance(data.remaining_balance || 0);
        }
      } catch (_e) {
        if (!cancelled) { setRows([]); setBalance(0); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authorizationId, refreshKey]);

  const reversalCount = rows.filter((r) => (r.type === 'refund' || r.type === 'void')).length;
  const originalCount = rows.filter((r) => r.type !== 'refund' && r.type !== 'void').length;

  if (loading) return null;
  if (rows.length === 0) return null;

  return (
    <div
      className="border border-slate-200 rounded-md bg-white overflow-hidden"
      data-testid={`cc-auth-reversal-history-${authorizationId}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-50"
        data-testid={`cc-auth-reversal-toggle-${authorizationId}`}
      >
        <RefreshCcw className="h-3 w-3" />
        <span className="flex-1 text-left">Reversal History</span>
        <span className="text-slate-500 normal-case tracking-normal font-normal">
          {originalCount} charge{originalCount !== 1 && 's'}
          {reversalCount > 0 && ` · ${reversalCount} reversal${reversalCount !== 1 ? 's' : ''}`}
        </span>
        <span className={`ml-2 font-mono ${balance <= 0 ? 'text-slate-500' : 'text-emerald-700'}`}>
          Remaining: {fmt(balance)}
        </span>
      </button>

      {expanded && (
        <div className="divide-y divide-slate-100">
          {rows.map((tx) => {
            const meta = rowMeta(tx);
            const Icon = meta.icon;
            return (
              <div
                key={tx.id}
                className={`px-3 py-2 text-[11px] flex flex-col gap-0.5 ${meta.bg}`}
                data-testid={`cc-auth-reversal-row-${tx.id}`}
              >
                <div className="flex items-center gap-2">
                  <Icon className={`h-3.5 w-3.5 ${meta.color}`} />
                  <span className={`font-semibold ${meta.color}`}>{meta.label}</span>
                  <span className="ml-auto font-mono font-semibold">
                    {tx.type === 'void' ? '—' : fmt(tx.amount)}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-slate-600 pl-5">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {fmtDT(tx.created_at)}
                  </span>
                  {tx.operator_email && (
                    <span className="inline-flex items-center gap-1">
                      <User className="h-3 w-3" /> {tx.operator_email}
                    </span>
                  )}
                  {tx.terminal_label && (
                    <span>· {tx.terminal_label}</span>
                  )}
                  {(tx.status || '').toLowerCase() === 'declined' && tx.error_message && (
                    <span className="text-rose-700">· {tx.error_message}</span>
                  )}
                </div>
                {tx.reason && (
                  <div className="pl-5 text-slate-600 italic">
                    Reason: {tx.reason}
                  </div>
                )}
                {tx.running_balance !== undefined && (
                  <div className="pl-5 text-[10px] text-slate-500">
                    Running balance after: <span className="font-mono">{fmt(tx.running_balance)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReversalHistoryBlock;
